import React, { useState, useEffect } from 'react';
import { theme } from '../styles/theme';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Select } from '../components/Select';
import { Badge } from '../components/Badge';
import { SettingsIcon, UserIcon } from '../components/Icons';
import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

interface BlockedUser {
  id: number;
  name: string;
  role: string;
  photo: string;
  blockedDate: string;
  reason: string;
}

type TabId = 'general' | 'profile';

export const SettingsPage = () => {
  // Determine initial tab from URL hash or default to 'general'
  const getInitialTab = (): TabId => {
    const hash = window.location.hash.replace('#', '');
    if (hash === 'profile') return 'profile';
    return 'general';
  };

  const [activeTab, setActiveTab] = useState<TabId>(getInitialTab());
  const [accountType, setAccountType] = useState('');
  const [memberSince, setMemberSince] = useState('');
  const [loading, setLoading] = useState(true);

  // General Tab - Account Settings
  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [twoFactorAuth, setTwoFactorAuth] = useState('disabled');

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    setLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.users.profile, {
        headers: getAuthHeaders(),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Failed to load profile:', data.error);
        setLoading(false);
        return;
      }

      const user = data.user;
      setCurrentEmail(user.email);
      setAccountType(user.role.charAt(0) + user.role.slice(1).toLowerCase());

      // Format member since date
      const createdDate = new Date(user.createdAt);
      const monthYear = createdDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      setMemberSince(monthYear);

      setLoading(false);
    } catch (err) {
      console.error('Fetch profile error:', err);
      setLoading(false);
    }
  };

  // General Tab - Privacy Settings
  const [showEmail, setShowEmail] = useState('no');
  const [allowMessages, setAllowMessages] = useState('all');
  const [showActivity, setShowActivity] = useState('yes');

  // Profile Tab - Visibility Settings
  const [profileVisibility, setProfileVisibility] = useState('public');
  const [showManuscripts, setShowManuscripts] = useState('yes');
  const [showBetaReaders, setShowBetaReaders] = useState('yes');
  const [showReadingHistory, setShowReadingHistory] = useState('members');
  const [searchableProfile, setSearchableProfile] = useState('yes');
  const [showStats, setShowStats] = useState('yes');

  // Profile Tab - Discoverability
  const [allowFriendRequests, setAllowFriendRequests] = useState('yes');
  const [showOnlineStatus, setShowOnlineStatus] = useState('yes');
  const [allowProfileSharing, setAllowProfileSharing] = useState('yes');

  // Profile Tab - Blocked Users
  // Note: Blocking feature is not yet implemented in the backend
  const [blockedUsers, setBlockedUsers] = useState<BlockedUser[]>([]);
  const [blockUserEmail, setBlockUserEmail] = useState('');

  // Update URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  const handleChangeEmail = async () => {
    if (!newEmail.trim() || !emailPassword.trim()) {
      window.alert('Please fill in all fields');
      return;
    }
    if (!newEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      window.alert('Please enter a valid email address');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.users.updateEmail, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          newEmail,
          password: emailPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to update email');
        return;
      }

      window.alert(data.message || 'Email updated successfully!');
      setCurrentEmail(newEmail);
      setNewEmail('');
      setEmailPassword('');
    } catch (err) {
      console.error('Change email error:', err);
      window.alert('Network error. Please try again.');
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
      window.alert('Please fill in all fields');
      return;
    }
    if (newPassword.length < 8) {
      window.alert('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      window.alert('New passwords do not match');
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.users.updatePassword, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        window.alert(data.error || 'Failed to update password');
        return;
      }

      window.alert('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Change password error:', err);
      window.alert('Network error. Please try again.');
    }
  };

  const handleEnable2FA = () => {
    if (twoFactorAuth === 'disabled') {
      window.alert('Two-factor authentication setup initiated. In a real app, this would show a QR code.');
      setTwoFactorAuth('enabled');
    } else {
      if (window.confirm('Disable two-factor authentication?')) {
        setTwoFactorAuth('disabled');
        window.alert('Two-factor authentication disabled.');
      }
    }
  };

  const handleSavePrivacySettings = () => {
    window.alert('Privacy settings saved successfully!');
  };

  const handleSaveVisibilitySettings = () => {
    window.alert('Profile visibility settings saved successfully!');
  };

  const handleSaveDiscoverabilitySettings = () => {
    window.alert('Discoverability settings saved successfully!');
  };

  const handleBlockUser = () => {
    if (!blockUserEmail.trim()) {
      window.alert('Please enter an email address or username');
      return;
    }
    if (window.confirm(`Block user "${blockUserEmail}"?`)) {
      const newBlockedUser: BlockedUser = {
        id: blockedUsers.length + 1,
        name: blockUserEmail,
        role: 'Unknown',
        photo: '',
        blockedDate: new Date().toISOString().split('T')[0],
        reason: 'Manually blocked',
      };
      setBlockedUsers([newBlockedUser, ...blockedUsers]);
      setBlockUserEmail('');
      window.alert('User blocked successfully');
    }
  };

  const handleUnblockUser = (userId: number, userName: string) => {
    if (window.confirm(`Unblock ${userName}?`)) {
      setBlockedUsers(blockedUsers.filter(user => user.id !== userId));
      window.alert(`${userName} has been unblocked`);
    }
  };

  const handleExportData = () => {
    if (window.confirm('Export all your account data? You will receive a download link via email within 24 hours.')) {
      window.alert('Data export requested! You will receive an email with a download link within 24 hours.');
    }
  };

  const handleDeleteAccount = () => {
    const confirmText = 'DELETE';
    const userInput = window.prompt(
      `Are you sure you want to permanently delete your account?\n\nThis action cannot be undone. All your data will be permanently deleted.\n\nType "${confirmText}" to confirm:`
    );
    if (userInput === confirmText) {
      window.alert('Account deletion initiated. You will receive a confirmation email.');
    } else if (userInput !== null) {
      window.alert('Account deletion cancelled.');
    }
  };

  const getProfileEditLink = () => {
    switch (accountType.toLowerCase()) {
      case 'writer':
        return '/writer/profile/edit';
      case 'reader':
        return '/reader/profile/edit';
      case 'agent':
        return '/agent/profile/edit';
      default:
        return '/writer/profile/edit';
    }
  };

  const tabs = [
    { id: 'general' as TabId, label: 'General', icon: <SettingsIcon width={20} height={20} /> },
    { id: 'profile' as TabId, label: 'Profile', icon: <UserIcon width={20} height={20} /> },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.background,
      fontFamily: theme.fonts.primary,
    }}>
      <Header />

      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '60px 40px',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: '40px',
            fontWeight: '400',
            marginBottom: '8px',
            color: theme.colors.textPrimary,
          }}>
            Settings
          </h1>
          <p style={{
            fontSize: '18px',
            color: theme.colors.textSecondary,
          }}>
            Manage your account, profile, and preferences
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '240px 1fr',
          gap: '32px',
        }}>
          {/* Left Sidebar - Tab Navigation */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          }}>
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '16px 20px',
                  backgroundColor: activeTab === tab.id ? theme.colors.primary : theme.colors.white,
                  color: activeTab === tab.id ? theme.colors.white : theme.colors.textPrimary,
                  borderRadius: theme.borderRadius.medium,
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: activeTab === tab.id ? '500' : '400',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  fontFamily: theme.fonts.primary,
                  border: activeTab === tab.id ? 'none' : `1px solid ${theme.colors.border}`,
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = theme.colors.backgroundAlt;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.backgroundColor = theme.colors.white;
                  }
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Right Content Area */}
          <div>
            {/* General Tab Content */}
            {activeTab === 'general' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Account Information */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '20px',
                  }}>
                    Account Information
                  </h3>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '200px 1fr',
                    gap: '16px',
                    alignItems: 'center',
                  }}>
                    <div style={{ fontSize: '14px', fontWeight: '500', color: theme.colors.textSecondary }}>
                      Account Type
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Badge variant="primary">{accountType}</Badge>
                      {memberSince && (
                        <span style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                          Member since {memberSince}
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: '14px', fontWeight: '500', color: theme.colors.textSecondary }}>
                      Account Status
                    </div>
                    <Badge variant="success">Active</Badge>

                    <div style={{ fontSize: '14px', fontWeight: '500', color: theme.colors.textSecondary }}>
                      Current Email
                    </div>
                    <div style={{ fontSize: '15px', color: theme.colors.textPrimary }}>
                      {currentEmail}
                    </div>
                  </div>
                </Card>

                {/* Change Email */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Change Email Address
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    You will need to verify your new email address
                  </p>

                  <Input
                    label="New Email Address"
                    type="email"
                    placeholder="Enter new email address"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    style={{ marginBottom: '16px' }}
                  />

                  <Input
                    label="Current Password"
                    type="password"
                    placeholder="Confirm with your password"
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    style={{ marginBottom: '16px' }}
                  />

                  <Button onClick={handleChangeEmail} disabled={!newEmail.trim() || !emailPassword.trim()}>
                    Change Email
                  </Button>
                </Card>

                {/* Change Password */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Change Password
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Choose a strong password with at least 8 characters
                  </p>

                  <Input
                    label="Current Password"
                    type="password"
                    placeholder="Enter your current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    style={{ marginBottom: '16px' }}
                  />

                  <Input
                    label="New Password"
                    type="password"
                    placeholder="Enter new password (min 8 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    style={{ marginBottom: '16px' }}
                  />

                  <Input
                    label="Confirm New Password"
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    helperText={newPassword && confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
                    style={{ marginBottom: '16px' }}
                  />

                  <Button
                    onClick={handleChangePassword}
                    disabled={!currentPassword.trim() || !newPassword.trim() || newPassword !== confirmPassword}
                  >
                    Change Password
                  </Button>
                </Card>

                {/* Two-Factor Authentication */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Two-Factor Authentication
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Add an extra layer of security to your account
                  </p>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    backgroundColor: theme.colors.backgroundAlt,
                    borderRadius: theme.borderRadius.medium,
                  }}>
                    <div>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        marginBottom: '4px',
                      }}>
                        Status: {twoFactorAuth === 'enabled' ? 'Enabled' : 'Disabled'}
                      </div>
                      <div style={{ fontSize: '14px', color: theme.colors.textSecondary }}>
                        {twoFactorAuth === 'enabled'
                          ? 'Your account is protected with 2FA'
                          : 'Enable 2FA for enhanced security'}
                      </div>
                    </div>
                    <Button
                      variant={twoFactorAuth === 'enabled' ? 'outline' : 'primary'}
                      onClick={handleEnable2FA}
                    >
                      {twoFactorAuth === 'enabled' ? 'Disable 2FA' : 'Enable 2FA'}
                    </Button>
                  </div>
                </Card>

                {/* Privacy Settings */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '20px',
                  }}>
                    Privacy Settings
                  </h3>

                  <Select
                    label="Show Email Address"
                    value={showEmail}
                    onChange={(e) => setShowEmail(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes - Show on profile' },
                      { value: 'no', label: 'No - Keep private' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Select
                    label="Allow Messages From"
                    value={allowMessages}
                    onChange={(e) => setAllowMessages(e.target.value)}
                    options={[
                      { value: 'all', label: 'Everyone' },
                      { value: 'connections', label: 'Connections Only' },
                      { value: 'none', label: 'No One' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Select
                    label="Show Activity Status"
                    value={showActivity}
                    onChange={(e) => setShowActivity(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes - Show when active' },
                      { value: 'no', label: 'No - Hide activity' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Button onClick={handleSavePrivacySettings}>
                    Save Privacy Settings
                  </Button>
                </Card>

                {/* Data & Export */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Your Data
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Download a copy of all your account data
                  </p>

                  <Button variant="outline" onClick={handleExportData}>
                    Request Data Export
                  </Button>
                </Card>

                {/* Delete Account */}
                <Card style={{
                  backgroundColor: '#FEF2F2',
                  border: `1px solid ${theme.colors.error}`,
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.error,
                    marginBottom: '8px',
                  }}>
                    Danger Zone
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Once you delete your account, there is no going back.
                  </p>

                  <Button variant="ghost" onClick={handleDeleteAccount} style={{ color: theme.colors.error }}>
                    Delete My Account
                  </Button>
                </Card>
              </div>
            )}

            {/* Profile Tab Content */}
            {activeTab === 'profile' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* Edit Profile Link */}
                <Card>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '500',
                        color: theme.colors.textPrimary,
                        marginBottom: '8px',
                      }}>
                        Edit Your Profile
                      </h3>
                      <p style={{
                        fontSize: '14px',
                        color: theme.colors.textSecondary,
                        margin: 0,
                      }}>
                        Update your bio, photo, and preferences
                      </p>
                    </div>
                    <Button onClick={() => window.location.href = getProfileEditLink()}>
                      Edit Profile →
                    </Button>
                  </div>
                </Card>

                {/* Profile Visibility */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Profile Visibility
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Control what information is visible on your profile
                  </p>

                  <Select
                    label="Who Can View Your Profile"
                    value={profileVisibility}
                    onChange={(e) => setProfileVisibility(e.target.value)}
                    options={[
                      { value: 'public', label: 'Public - Anyone' },
                      { value: 'members', label: 'Members Only' },
                      { value: 'connections', label: 'Connections Only' },
                      { value: 'private', label: 'Private - Only me' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  {accountType === 'Writer' && (
                    <>
                      <Select
                        label="Show My Manuscripts"
                        value={showManuscripts}
                        onChange={(e) => setShowManuscripts(e.target.value)}
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                        ]}
                        style={{ marginBottom: '16px' }}
                      />

                      <Select
                        label="Show My Beta Readers"
                        value={showBetaReaders}
                        onChange={(e) => setShowBetaReaders(e.target.value)}
                        options={[
                          { value: 'yes', label: 'Yes' },
                          { value: 'no', label: 'No' },
                        ]}
                        style={{ marginBottom: '16px' }}
                      />
                    </>
                  )}

                  {accountType === 'Reader' && (
                    <Select
                      label="Show My Reading History"
                      value={showReadingHistory}
                      onChange={(e) => setShowReadingHistory(e.target.value)}
                      options={[
                        { value: 'yes', label: 'Yes - Show to everyone' },
                        { value: 'members', label: 'Members Only' },
                        { value: 'no', label: 'No - Keep private' },
                      ]}
                      style={{ marginBottom: '16px' }}
                    />
                  )}

                  <Select
                    label="Show Profile Statistics"
                    value={showStats}
                    onChange={(e) => setShowStats(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Button onClick={handleSaveVisibilitySettings}>
                    Save Visibility Settings
                  </Button>
                </Card>

                {/* Discoverability */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Discoverability
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Control how others can find and interact with you
                  </p>

                  <Select
                    label="Searchable Profile"
                    value={searchableProfile}
                    onChange={(e) => setSearchableProfile(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes - Appear in searches' },
                      { value: 'no', label: 'No - Hide from searches' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Select
                    label="Allow Connection Requests"
                    value={allowFriendRequests}
                    onChange={(e) => setAllowFriendRequests(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Select
                    label="Show Online Status"
                    value={showOnlineStatus}
                    onChange={(e) => setShowOnlineStatus(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Select
                    label="Allow Profile Sharing"
                    value={allowProfileSharing}
                    onChange={(e) => setAllowProfileSharing(e.target.value)}
                    options={[
                      { value: 'yes', label: 'Yes' },
                      { value: 'no', label: 'No' },
                    ]}
                    style={{ marginBottom: '16px' }}
                  />

                  <Button onClick={handleSaveDiscoverabilitySettings}>
                    Save Discoverability Settings
                  </Button>
                </Card>

                {/* Blocked Users */}
                <Card>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '500',
                    color: theme.colors.textPrimary,
                    marginBottom: '8px',
                  }}>
                    Blocked Users
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: theme.colors.textSecondary,
                    marginBottom: '20px',
                  }}>
                    Blocked users cannot message you or view your profile
                  </p>

                  {/* Block New User */}
                  <div style={{
                    padding: '20px',
                    backgroundColor: theme.colors.backgroundAlt,
                    borderRadius: theme.borderRadius.medium,
                    marginBottom: '20px',
                  }}>
                    <div style={{
                      fontSize: '15px',
                      fontWeight: '500',
                      color: theme.colors.textPrimary,
                      marginBottom: '12px',
                    }}>
                      Block a User
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'flex-end',
                    }}>
                      <div style={{ flex: 1 }}>
                        <Input
                          label="Email or Username"
                          placeholder="Enter email or username"
                          value={blockUserEmail}
                          onChange={(e) => setBlockUserEmail(e.target.value)}
                        />
                      </div>
                      <Button onClick={handleBlockUser} disabled={!blockUserEmail.trim()}>
                        Block User
                      </Button>
                    </div>
                  </div>

                  {/* Blocked Users List */}
                  {blockedUsers.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      backgroundColor: theme.colors.backgroundAlt,
                      borderRadius: theme.borderRadius.medium,
                    }}>
                      <div style={{ fontSize: '32px', marginBottom: '12px' }}>✓</div>
                      <div style={{ fontSize: '16px', color: theme.colors.textSecondary }}>
                        No blocked users
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {blockedUsers.map((user) => (
                        <div
                          key={user.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '16px',
                            backgroundColor: theme.colors.white,
                            border: `1px solid ${theme.colors.border}`,
                            borderRadius: theme.borderRadius.medium,
                          }}
                        >
                          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flex: 1 }}>
                            <div style={{
                              width: '48px',
                              height: '48px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.backgroundAlt,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              <UserIcon width={24} height={24} />
                            </div>
                            <div>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                marginBottom: '4px',
                              }}>
                                <div style={{
                                  fontSize: '16px',
                                  fontWeight: '500',
                                  color: theme.colors.textPrimary,
                                }}>
                                  {user.name}
                                </div>
                                <Badge variant="default">{user.role}</Badge>
                              </div>
                              <div style={{ fontSize: '13px', color: theme.colors.textSecondary }}>
                                Blocked on {new Date(user.blockedDate).toLocaleDateString()}
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => handleUnblockUser(user.id, user.name)}
                          >
                            Unblock
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        </div>

        {/* Back Button */}
        <div style={{ marginTop: '32px' }}>
          <Button
            variant="ghost"
            onClick={() => window.location.href = '/writer/dashboard'}
          >
            ← Back to Dashboard
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};
